import React, { useState } from 'react';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  FormControl,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { CSSProperties } from 'react';

type Option = {
  label: string;
  color: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | string;
};

export interface Props {
  label?: string;
  options: Option[];
  onClick: (value: Option) => void;
  size?: 'small' | 'medium' | 'large';
  customStyle?: CSSProperties;
  readonly?: boolean;
  disabled?: boolean;
  name?: string;
  defaultValue?: Option;
  className?: string;
}

export const DropdownButtonComponent = ({
  className,
  label,
  options,
  onClick,
  customStyle,
  size = 'medium',
  readonly,
  disabled,
  name,
  defaultValue,
}: Props) => {
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [selectedOption, setSelectedOption] = useState<Option>(
    defaultValue || options[0]
  );

  const handleButtonClick = () => {
    if (selectedOption && onClick) {
      onClick(selectedOption);
    }
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setSelectedOption(options[index]);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  return (
    <FormControl
      style={{ minWidth: '10em', marginLeft: '0.5em', ...customStyle }}
      variant="outlined"
      size="small"
    >
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label={label || 'split button'}
        disabled={disabled}
        style={{ ...customStyle, boxShadow: 'none' }}
        color={selectedOption.color as any}
      >
        <Button onClick={handleButtonClick}>{selectedOption.label || label}</Button>
        <Button
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select option"
          aria-haspopup="menu"
          onClick={handleToggle}
          style={{
            minWidth: "1.125rem",
            maxWidth: "1.125rem",
          }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        style={{ zIndex: 11 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.label}
                      selected={option === selectedOption}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </FormControl>
  );
};
